import type { LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import { SURFACE } from '../../mesh/attributes';
import { PATTERN } from '../../../render/patterns/patternIds';
import type { Frame, MeshBuilder } from '../../mesh/meshBuilder';
import {
  horizontalCap,
  orientedFace,
  prismSolid,
  planPoint,
  pyramidOverRing,
  ringPoints,
  triangulateRing,
} from '../solids';
import { buildVessel } from './churchVessel';
import {
  edgePanel,
  openedWall,
  pointedWindows,
  rectWall,
  roofUv,
  type MasonryStyles,
} from './masonry';
import { corners } from './genericBlock';
import { buildConventRange } from './conventRange';
import reading from './ursulaPlan.json';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { meshFrameOf } from './planFrame';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';
import type { LandmarkContext } from './landmarkFeature';
import { ursulaEntryHeight, ursulaFloors, ursulaFrame, ursulaPartFrame } from './ursulaPlan';
import { layoutToWorld } from '../../../core/coords';

/** The low parallel body meets the nave under a single slope, rather than another full chapel roof. */
function sideWing(builder: MeshBuilder, frame: Frame, styles: MasonryStyles): void {
  const wing = reading.sideWing,
    ring = corners(wing),
    y = (v: number) => wing.high + ((wing.eaves - wing.high) * (v - wing.v0)) / (wing.v1 - wing.v0);
  ring.forEach((from, side) => {
    const to = ring[(side + 1) % ring.length]!,
      length = Math.hypot(to[0] - from[0], to[1] - from[1]);
    if (side % 2 === 0)
      rectWall(
        builder,
        styles,
        frame,
        [from, to],
        [0, y(from[1])],
        side === 2 ? pointedWindows(length, length / wing.bays, 1.1, 3.2) : []
      );
    else
      openedWall(
        builder,
        styles,
        frame,
        edgePanel(from, to),
        [
          [0, 0],
          [length, 0],
          [length, y(to[1])],
          [0, y(from[1])],
        ],
        []
      );
  });
  const top = ring.map(([u, v]) => planPoint(u, y(v), v));
  orientedFace(builder, styles.roof, frame, top, [0, 1, -1], roofUv(top[0]!, top[1]!, top[3]!));
  horizontalCap(builder, styles.wall, frame, ring, 0.01, true);
}

/** Source-read Ursula bodies, open garden and the documented southern rental use. */
export function buildUrsulaConvent(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: PaintedChapelColors & { tile: LinearRGB; ground: LinearRGB; garden: LinearRGB },
  ids: { edgeId: number; seed: number },
  tracery: TracerySink = NO_TRACERY,
  context: Pick<LandmarkContext, 'elevation' | 'elevationAt'> = {}
): void {
  const plan = ursulaFrame(landmark),
    styles = paintedChapelStyles(colors, ids, tracery),
    brick = { ...styles, wall: { ...styles.wall, surface: SURFACE.brick } },
    chapel = reading.chapel,
    chapelFrame = meshFrameOf(ursulaPartFrame(plan, chapel));
  buildVessel(
    builder,
    brick,
    chapelFrame,
    {
      u0: -chapel.length / 2,
      chordU: chapel.length / 2,
      vc: 0,
      half: chapel.width / 2,
      eaveY: chapel.eaves,
      ridgeY: chapel.ridge,
      apse: 'flat',
    },
    {
      longWindows: { sill: 6.4, top: chapel.eaves - 1.1 },
      westWindow: true,
      bay: chapel.length / chapel.bays,
    }
  );
  const bell = reading.belfry,
    bellRing = ringPoints([bell.u, 0], bell.radius, 8, Math.PI / 8);
  bellRing.forEach((from, i) => {
    const to = bellRing[(i + 1) % bellRing.length]!,
      width = Math.hypot(to[0] - from[0], to[1] - from[1]);
    rectWall(
      builder,
      brick,
      chapelFrame,
      [from, to],
      [bell.foot, bell.eaves],
      pointedWindows(width, width, 0.5, bell.eaves - bell.foot - 0.2),
      bell.foot
    );
  });
  horizontalCap(builder, brick.wall, chapelFrame, bellRing, bell.foot, false);
  pyramidOverRing(builder, brick, chapelFrame, bellRing, bell.eaves, {
    plan: [bell.u, 0],
    y: bell.tip,
  });
  sideWing(builder, meshFrameOf(plan), brick);
  for (const part of [...reading.ranges, ...reading.rentals])
    buildConventRange(
      builder,
      meshFrameOf(ursulaPartFrame(plan, part)),
      part.roof === 'tile'
        ? {
            ...brick,
            roof: {
              ...brick.roof,
              color: colors.tile,
              surface: SURFACE.roofTile,
              pattern: PATTERN.roofTileHollePan,
            },
          }
        : brick,
      part
    );

  const floors = ursulaFloors(landmark),
    base = context.elevation ?? 0,
    terrain = context.elevationAt ?? (() => base),
    worldFrame: Frame = { origin: [0, 0, 0], yaw: 0 },
    garden = {
      ...brick.wall,
      color: colors.garden,
      surface: SURFACE.garden,
      pattern: PATTERN.none,
    },
    ground = {
      ...brick.wall,
      color: colors.ground,
      surface: SURFACE.street,
      pattern: PATTERN.none,
    };
  for (const floor of [floors.court, ...floors.beds]) {
    // Close the level court down to terrain; a shallow cap must not float above a lower bank.
    const ring = floor.ring;
    prismSolid(
      builder,
      floor === floors.court ? ground : garden,
      worldFrame,
      ring,
      Math.min(...ring.map(terrain)) - base - 0.05,
      floor.height,
      { top: true, bottom: false }
    );
  }
  const entryFrame = ursulaPartFrame(plan, floors.entry.part);
  const entryY = (p: readonly [number, number]) =>
    ursulaEntryHeight(entryFrame, floors.entry.part, p, terrain, base) - base;
  for (const tri of triangulateRing(floors.entry.ring))
    orientedFace(
      builder,
      ground,
      worldFrame,
      tri.map((p) => layoutToWorld(p, entryY(p))),
      [0, 1, 0]
    );
  const bottom = Math.min(...floors.entry.ring.map(terrain)) - base - 0.05;
  floors.entry.ring.forEach((from, i) => {
    const to = floors.entry.ring[(i + 1) % floors.entry.ring.length]!;
    orientedFace(
      builder,
      ground,
      worldFrame,
      [
        layoutToWorld(from, bottom),
        layoutToWorld(to, bottom),
        layoutToWorld(to, entryY(to)),
        layoutToWorld(from, entryY(from)),
      ],
      [to[1] - from[1], 0, to[0] - from[0]]
    );
  });
  horizontalCap(builder, ground, worldFrame, floors.entry.ring, bottom, false);
}
